import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useInView } from 'react-intersection-observer'
import Image from 'components/Common/Image'
import { rem } from 'polished'
import Clickable from 'components/Common/Clickable'
import clsx from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import type { TrackingProps } from 'contexts/trackingContext'
import TrackingContext from 'contexts/trackingContext'
import * as Analytics from 'analytics/analytics'
import { trackCruisesClickEvent } from 'lib/cruises/cruiseUtils'

const CUNARD_VENDOR_CODE = 'CUN'
const DEFAULT_LOGO_PADDING = 16
const CUNARD_LOGO_PADDING = 30

const ImageButton = styled(Clickable)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  isolation: isolate;
  height: ${rem(80)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;

  ${mediaHoverable} {
    &:hover {
      box-shadow: inset 0px 0px 0px 1px ${props => props.theme.palette.neutral.default.one};
    }
  }

  &.selected {
    box-shadow: inset 0px 0px 0px 2px ${props => props.theme.palette.neutral.default.one};
  }

  &.quick-filter-item {
    width: ${rem(110)};
  }

  ${mediaQueryUp.desktop} {
    height: ${rem(100)};

    &.quick-filter-item {
      width: auto;
    }
  }
`

interface Props {
  cruiseLine: App.CruiseSearchFacet
  isSelected?: boolean
  to?: string
  isQuickFilter?: boolean
}

function CruiseLinesSectionItem(props: Props) {
  const { to, cruiseLine, isSelected, isQuickFilter } = props

  const tracking: TrackingProps | undefined = useContext(TrackingContext)
  const [inViewRef, trackingInView] = useInView({ triggerOnce: true, threshold: 1 })

  const cruiseLineSubject = useMemo(() =>
    `Cruise Page Lines - ${cruiseLine.name}`,
  [cruiseLine.name],
  )

  const onClick = useCallback(() => {
    if (tracking?.listName) {
      trackCruisesClickEvent(cruiseLineSubject)
    }
  }, [cruiseLineSubject, tracking])

  useEffect(() => {
    if (trackingInView && tracking?.listName) {
      Analytics.trackClientEvent({
        subject: cruiseLineSubject,
        action: 'viewed',
        category: 'cruise',
        type: 'interaction',
      })
    }
  }, [trackingInView, cruiseLineSubject, tracking])

  const imagePadding = cruiseLine.value === CUNARD_VENDOR_CODE
    ? CUNARD_LOGO_PADDING
    : DEFAULT_LOGO_PADDING

  return (
    <ImageButton
      ref={inViewRef}
      onClick={onClick}
      to={to}
      className={clsx({
        'quick-filter-item': isQuickFilter,
        selected: isSelected,
      })}
    >
      <Image
        id={cruiseLine.imageId}
        alt={cruiseLine.name}
        style={{
          width: '100%',
          padding: imagePadding,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />
    </ImageButton>
  )
}

export default CruiseLinesSectionItem
